import React, { useState } from 'react';
import { Mail, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useRequestPasswordReset } from '../hooks/usePasswordReset';

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const requestPasswordReset = useRequestPasswordReset();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError('Please enter your email address.');
      return;
    }

    try {
      await requestPasswordReset.mutateAsync({ email: normalizedEmail });
      navigate('/login', {
        replace: true,
        state: {
          message:
            'If an account with that email exists, check your inbox for password reset instructions.',
          email: normalizedEmail,
        },
      });
    } catch (mutationError) {
      setError(mutationError.message || 'Failed to send password reset email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 rounded-2xl blur-3xl" />
        <div className="absolute inset-0 bg-slate-900/80 rounded-2xl backdrop-blur-sm" />

        <div className="relative bg-slate-900/50 p-6 sm:p-8 rounded-2xl border border-slate-800 shadow-2xl">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="flex items-center text-sm text-gray-400 hover:text-gray-200 mb-4 touch-manipulation"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to sign in
          </button>

          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
              Forgot your password?
            </h2>
            <p className="mt-2 text-sm sm:text-base text-gray-400">
              Enter your email and we&apos;ll send you instructions to reset your password.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-medium text-gray-300">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="block w-full pl-9 sm:pl-10 pr-3 py-3 sm:py-3.5 bg-slate-800/50 border border-slate-700 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-gray-500 text-gray-100 text-sm sm:text-base"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={requestPasswordReset.isPending}
              className="w-full py-3 sm:py-3.5 px-4 rounded-lg relative overflow-hidden group touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-400 transition-transform duration-300 group-hover:scale-105 group-disabled:scale-100" />
              <span className="relative text-white font-semibold group-hover:scale-105 transition-transform duration-300 inline-block text-sm sm:text-base">
                {requestPasswordReset.isPending ? 'Sending...' : 'Send reset link'}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
